import React, { useState } from 'react';
import { requestPasswordReset, updatePassword, verifyPasswordOtp } from '../../utils/api';
import { trackForgotPasswordCompleted } from '../../utils/analytics';

interface ForgotPasswordProps {
  onContinue?: (step: number) => void;
  onBackToLogin?: () => void;
  onClose?: () => void;
}

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onContinue, onBackToLogin }) => {
  const [step, setStep] = useState<'forgot' | 'verify' | 'newPassword'>('forgot');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '']);
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  const showToast = (message: string) => {
    setToastMessage(message);
    setTimeout(() => {
      setToastMessage('');
      if (onBackToLogin) {
        onBackToLogin();
      }
    }, 2000);
  };

  const handleSendCode = async () => {
    if (!email) return setError('Please check your email');
    setLoading(true);
    setError('');
    try {
      await requestPasswordReset(email);
      setStep('verify');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const code = otp.join('');
    if (code.length !== 5) return setError('Enter 5-digit code');
    setLoading(true);
    setError('');
    try {
      const data = await verifyPasswordOtp(email, code);
      if (data.token) {
        setToken(data.token);
        setStep('newPassword');
      } else {
        setError('Invalid OTP code');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!newPassword) return setError('Enter new password');
    if (confirmPassword !== newPassword) return setError('Passwords do not match');
    setLoading(true);
    setError('');
    try {
      await updatePassword(token, newPassword);
      trackForgotPasswordCompleted();
      showToast('Password updated successfully');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index: number, value: string) => {
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      if (value && index < otp.length - 1) {
        const nextInput = document.querySelector<HTMLInputElement>(`.otp-inputs input:nth-child(${index + 2})`);
        if (nextInput) nextInput.focus();
      }
    }
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector<HTMLInputElement>(`.otp-inputs input:nth-child(${index})`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
    if (!pastedData) return;

    const newOtp = [...otp];
    for (let i = 0; i < otp.length; i++) {
      newOtp[i] = pastedData[i] || '';
    }
    setOtp(newOtp);

    const lastFilledIndex = Math.min(pastedData.length, otp.length) - 1;
    const nextInput = document.querySelector<HTMLInputElement>(`.otp-inputs input:nth-child(${lastFilledIndex + 1})`);
    if (nextInput) nextInput.focus();
  };

  return (
    <div className="w-full">
      <div className="w-full">
        <div className="w-full">
          <div className="text-center mb-[30px]">
            <h2 className="text-white text-center font-poppins text-[28px] font-semibold leading-[120%] capitalize mb-[10px]">
              {step === 'forgot' ? 'Forgot Password' : step === 'verify' ? 'Verify Code' : 'Set New Password'}
            </h2>
            <p className="text-white font-poppins text-base font-semibold leading-[120%] capitalize mb-5 flex items-center justify-center gap-[3px]">
              {step === 'forgot' && 'Enter your email address'}
              {step === 'verify' && 'Enter the code sent to your email'}
              {step === 'newPassword' && 'Enter your new password'}
            </p>
            <button
              className="hidden"
              onClick={() => {
                if (onContinue) onContinue(17);
              }}
            >
              ×
            </button>
          </div>
          <form onSubmit={(e) => e.preventDefault()} className="w-full max-w-[330px] mx-auto">
            {step === 'forgot' && (
              <>
                <div className="relative w-full pb-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="rounded-[18px] border border-white bg-white/10 px-4 py-4 text-white font-poppins text-base font-normal leading-[120%] w-full h-[46px] transition-all duration-300 placeholder:text-white focus:outline-none focus:border-[#fb6284] focus:bg-white/15"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                {error && <div className="text-[#ff4444] font-poppins text-sm font-normal leading-[120%] text-start -mt-1 mb-4">{error}</div>}

                <button
                  type="button"
                  className="rounded-[18px] bg-gradient-to-r from-[#8350cb] to-[#fe6283] shadow-[0_4px_16.8px_0_rgba(240,73,243,0.47)] border-none px-[15px] py-[15px] text-white text-base font-semibold cursor-pointer transition-all duration-300 w-full mb-5 hover:bg-[#2a2a2a] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={handleSendCode}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Code'}
                </button>

                <div onClick={onBackToLogin} className="text-[#8b8f9c] font-poppins text-[10px] font-normal leading-[120%] text-center mt-5">
                  Back to{' '}
                  <button type="button" className="text-[#e0dff5] font-poppins bg-transparent text-[10px] font-medium leading-[120%] border-none cursor-pointer transition-colors duration-300">
                    Log in &gt;
                  </button>
                </div>
              </>
            )}

            {step === 'verify' && (
              <>
                <div className="otp-inputs flex items-start gap-2 w-full self-stretch pb-7">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      className="flex h-[74px] px-3 justify-center items-center text-center rounded-lg border border-white/[0.12] bg-white/[0.12] text-[#d9d1dc] font-poppins text-[28px] font-semibold leading-[140%] tracking-[-0.56px] flex-1 w-full placeholder:text-[#574f5e] focus:rounded-lg focus:outline-none"
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={handleOtpPaste}
                    />
                  ))}
                </div>
                {error && <div className="text-[#ff4444] font-poppins text-sm font-normal leading-[120%] text-start -mt-1 mb-4">{error}</div>}

                <button
                  type="button"
                  className="rounded-[18px] bg-gradient-to-r from-[#8350cb] to-[#fe6283] shadow-[0_4px_16.8px_0_rgba(240,73,243,0.47)] border-none px-[15px] py-[15px] text-white text-base font-semibold cursor-pointer transition-all duration-300 w-full mb-5 hover:bg-[#2a2a2a] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={handleVerifyCode}
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Apply Code'}
                </button>

                <div className="text-[#8b8f9c] font-poppins text-[10px] font-normal leading-[120%] text-center mt-5">
                  <button type="button" className="text-[#e0dff5] font-poppins bg-transparent text-[10px] font-medium leading-[120%] border-none cursor-pointer transition-colors duration-300" onClick={handleSendCode}>
                    Resend Code
                  </button>
                </div>
              </>
            )}

            {step === 'newPassword' && (
              <>
                <div className="relative w-full pb-2">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="New Password"
                    className="rounded-[18px] border border-white bg-white/10 px-4 py-4 text-white font-poppins text-base font-normal leading-[120%] w-full h-[46px] transition-all duration-300 placeholder:text-white focus:outline-none focus:border-[#fb6284] focus:bg-white/15"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute top-1/2 -translate-y-1/2 right-4 cursor-pointer bg-none border-none p-0"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z"
                          stroke="#9B9CAA"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z"
                          stroke="#9B9CAA"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421"
                          stroke="#9B9CAA"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="relative w-full pb-2">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirm Password"
                    className="rounded-[18px] border border-white bg-white/10 px-4 py-4 text-white font-poppins text-base font-normal leading-[120%] w-full h-[46px] transition-all duration-300 placeholder:text-white focus:outline-none focus:border-[#fb6284] focus:bg-white/15"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute top-1/2 -translate-y-1/2 right-4 cursor-pointer bg-none border-none p-0"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M8 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203C12.7169 10.6667 11.4036 12.6667 8 12.6667C4.59644 12.6667 2.36319 9.66344 1.61292 8.47545C1.52212 8.33168 1.47672 8.25979 1.45131 8.14891C1.43221 8.06563 1.43222 7.93426 1.45133 7.85099C1.47675 7.74011 1.52236 7.66791 1.61357 7.52352C2.36369 6.33656 4.59694 3.33333 8 3.33333Z"
                          stroke="#9B9CAA"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 6C9.10457 6 10 6.89543 10 8C10 9.10457 9.10457 10 8 10C6.89543 10 6 9.10457 6 8C6 6.89543 6.89543 6 8 6Z"
                          stroke="#9B9CAA"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M7.16196 3.39488C7.4329 3.35482 7.7124 3.33333 8.00028 3.33333C11.4036 3.33333 13.6369 6.33656 14.3871 7.52455C14.4779 7.66833 14.5233 7.74023 14.5488 7.85112C14.5678 7.93439 14.5678 8.06578 14.5487 8.14905C14.5233 8.25993 14.4776 8.3323 14.3861 8.47705C14.1862 8.79343 13.8814 9.23807 13.4777 9.7203M4.48288 4.47669C3.0415 5.45447 2.06297 6.81292 1.61407 7.52352C1.52286 7.66791 1.47725 7.74011 1.45183 7.85099C1.43273 7.93426 1.43272 8.06563 1.45181 8.14891C1.47722 8.25979 1.52262 8.33168 1.61342 8.47545C2.36369 9.66344 4.59694 12.6667 8.00028 12.6667C9.37255 12.6667 10.5546 12.1784 11.5259 11.5177M2.00028 2L14.0003 14M6.58606 6.58579C6.22413 6.94772 6.00028 7.44772 6.00028 8C6.00028 9.10457 6.89571 10 8.00028 10C8.55256 10 9.05256 9.77614 9.41449 9.41421"
                          stroke="#9B9CAA"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                {error && <div className="text-[#ff4444] font-poppins text-sm font-normal leading-[120%] text-start -mt-1 mb-4">{error}</div>}

                <button
                  type="button"
                  className="rounded-[18px] bg-gradient-to-r from-[#8350cb] to-[#fe6283] shadow-[0_4px_16.8px_0_rgba(240,73,243,0.47)] border-none px-[15px] py-[15px] text-white text-base font-semibold cursor-pointer transition-all duration-300 w-full mb-5 hover:bg-[#2a2a2a] hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={handleUpdatePassword}
                  disabled={loading}
                >
                  {loading ? 'Updating...' : 'Update Password'}
                </button>
              </>
            )}
          </form>
          {step === 'newPassword' && toastMessage && (
            <div className="fixed top-5 right-5 bg-[#322636] text-white border border-[#463046] rounded-[20px] px-6 py-4 text-base shadow-[0_20px_40px_rgba(0,0,0,0.5)] flex items-center gap-3 z-[9999]">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="white" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
              {toastMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
